import { useEffect, useRef, useState } from "react";
import { Alert, Box, Stack, Typography } from "@mui/material";

import * as yup from "yup";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getCurrentUser, confirmSignIn } from "aws-amplify/auth";

import { PASSWORD } from "@helpers/regex";
import { useLazyGetCurrentUserInfoV2Query } from "@services/api/users";
import { setSignInStep, verifiedOTP } from "@store/slices/sessionSlice";

import { ArrowRight } from "lucide-react";

import { AppButton } from "@components/v2/AppButton";
import { PasswordField } from "./PasswordField";
import { primaryButtonSx } from "./loginStyles";

export const FormularioCambioContrasenia = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [error, setError] = useState<boolean>(false);

    const hasFetched = useRef(false);

    const [triggerGetCurrentUserInfo] = useLazyGetCurrentUserInfoV2Query();

    const formik = useFormik({
        initialValues: {
            password: "",
            password_confirm: "",
        },
        validationSchema: yup.object().shape({
            password: yup
                .string()
                .matches(
                    PASSWORD,
                    "Formato incorrecto. Debe contener una letra mayúscula, número, carácter especial y más de 8 caracteres.",
                )
                .required("Campo requerido"),
            password_confirm: yup
                .string()
                .matches(
                    PASSWORD,
                    "Formato incorrecto. Debe contener una letra mayúscula, número, carácter especial y más de 8 caracteres.",
                )
                .required("Campo requerido"),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);
                await confirmSignIn({
                    challengeResponse: values.password,
                });

                const currentUser = await getCurrentUser();

                await triggerGetCurrentUserInfo(currentUser.userId).unwrap();
                dispatch(verifiedOTP());
                dispatch(setSignInStep("WELCOME"));
                navigate("/solicitudes");
            } catch (error) {
            } finally {
                setSubmitting(false);
            }
        },
    });

    useEffect(() => {
        setError(formik.values.password !== formik.values.password_confirm);
    }, [formik.values]);

    return (
        <form onSubmit={formik.handleSubmit} noValidate>
            <Stack direction={"column"} sx={{ gap: 2.5 }}>
                <Box>
                    <Typography
                        sx={{
                            fontFamily: "'Outfit', system-ui, sans-serif",
                            fontSize: "32px",
                            fontWeight: 600,
                            letterSpacing: "-0.02em",
                            color: "text.primary",
                        }}>
                        ¡Hola!
                    </Typography>
                    <Typography sx={{ fontSize: "15px", color: "text.secondary", mt: 1 }}>
                        Actualiza tu contraseña
                    </Typography>
                </Box>
                <PasswordField
                    id="password"
                    label="Contraseña"
                    required
                    fullWidth
                    size="small"
                    autoComplete="new-password"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={(formik.touched.password && Boolean(formik.errors.password)) ?? false}
                    helperText={formik.touched.password && formik.errors.password}
                    value={formik.values.password}
                />
                <PasswordField
                    id="password_confirm"
                    label="Confirma tu contraseña"
                    required
                    fullWidth
                    size="small"
                    autoComplete="new-password"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={(formik.touched.password_confirm && Boolean(formik.errors.password_confirm)) ?? false}
                    helperText={formik.touched.password_confirm && formik.errors.password_confirm}
                />

                {error && (
                    <Alert variant="outlined" severity="error">
                        Las contraseñas no coinciden
                    </Alert>
                )}
                <AppButton
                    tone="navy"
                    type="submit"
                    size="large"
                    disabled={error}
                    loading={formik.isSubmitting}
                    endIcon={<ArrowRight size={18} strokeWidth={2.2} />}
                    sx={primaryButtonSx}
                >
                    Actualizar contraseña
                </AppButton>
            </Stack>
        </form>
    );
};
